import { useContext, useRef } from 'react'

import HomeIcon from '@/public/icons/home_line.svg'
import WarningIcon from '@/public/images/warning.svg'
import {
  Breadcrumbs,
  Container,
  Heading,
  useHealthToolsCache,
} from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { getMarryBabyUrl, redirectToSSOWithReturnUrl } from 'api/utils'
import { TwoColumnsContainer } from 'components/molecules'
import { AdBlock } from 'components/molecules/adBlock'
import { FixedBottomAdBlock } from 'components/molecules/adBlock/fixedBottomAdBlock'
import { OvulationInfo } from 'components/templates/ovulationCalculator/ovulationInfo'
import { domainLocales } from 'domainLocales'
// import { useIsMobile } from 'hooks/useIsMobile'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath, HEALTH_TOOL_TYPE } from 'interfaces/constants'
import { BabyPoopPage } from 'interfaces/types'
import { useIntl } from 'react-intl'
import { AppContext } from 'state/appContext'
import { getAdTag, getCountryAdTag, getLocaleCategoryAdTag } from 'utils/ads'

import { BabyPoopHeader, BabyPoopListItem } from '../components'
import {
  StyledBabyPoopInfo,
  StyledBabyPoopIntroduceParsedContent,
} from '../components/index.styled'
import { StyledDisclamerInfo } from '../detail/index.styled'
import {
  StyledBabyPoopIntroduceSubColumn,
  StyledBreadCrumbsContainer,
} from './index.styled'
import {
  StyledBabyPoopContentWarning,
  StyledBabyPoopIntroduce,
  StyledBabyPoopIntroduceMainColumn,
  // StyledBabyPoopIntroduceSubColumn,
  StyledBabyPoopListDescription,
  StyledBabyPoopListGrid,
  StyledContent,
  StyledGuideline,
} from './index.styled'

type Props = {
  babyPoopPage: BabyPoopPage
}

const BabyPoopIntroduce = ({ babyPoopPage }: Props) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()

  const { locale, asPath } = useRouterExtend()
  const {
    state: { isLoggedIn },
  } = useContext(AppContext)
  const warning = useRef<HTMLDivElement>(null)
  const { getHealthToolLink } = useHealthToolsCache(locale + '_MB')

  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: getLocaleCategoryAdTag(locale, asPath) || {
      result: 'HealthTools',
      redundant: true,
    },
  })

  return (
    <StyledBabyPoopIntroduce>
      <div>
        <StyledBreadCrumbsContainer className="lightpink">
          <Container>
            <Breadcrumbs
              size="sm"
              className="breadcrumbs"
              pages={[
                {
                  label: <HomeIcon />,
                  value: '/',
                  dataEventCategory: 'Breadcrumb',
                  dataEventAction: 'Click',
                  dataEventLabel: `https://${domainLocales[locale]}`,
                },
                {
                  label: f({ id: 'healthtools.title' }),
                  value: `https://${domainLocales['vi-VN']}${basePath}/health-tools`,
                  dataEventCategory: 'Breadcrumb',
                  dataEventAction: 'Click',
                  dataEventLabel: `https://${domainLocales['vi-VN']}${basePath}/health-tools`,
                },
                {
                  label: babyPoopPage?.title || '',
                  active: true,
                  value: '#',
                  dataEventCategory: 'Breadcrumb',
                },
              ]}
            />
          </Container>
        </StyledBreadCrumbsContainer>
        <BabyPoopHeader
          babyPoopPage={babyPoopPage}
          onClickFindOut={() => {
            if (warning.current) {
              window.scrollTo({
                top: warning.current.getBoundingClientRect().top - 80,
                behavior: 'smooth',
              })
            }
          }}
          style={{
            padding: '16px 0 40px',
          }}
        />
      </div>

      <Container className="px-16">
        <TwoColumnsContainer
          isHavingBorder={false}
          // leftStyle={{ paddingRight: 16, width: '63%' }}
          leftColumn={
            <StyledBabyPoopIntroduceMainColumn>
              <StyledContent>
                <StyledBabyPoopIntroduceParsedContent>
                  <Text size="p2" as="div">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: babyPoopPage.content.replace(
                          /<(\/)?h1>/gi,
                          '<$1h3>',
                        ),
                      }}
                    />
                  </Text>
                </StyledBabyPoopIntroduceParsedContent>

                {isMobile && (
                  <div className="top-ad">
                    <AdBlock
                      adUnitPath={adUnitPath}
                      size={[
                        [300, 250],
                        [300, 600],
                      ]}
                      divId={'div-gpt-ad-midbanner1-mobile'}
                      pos={['MidBanner1']}
                    />
                  </div>
                )}

                {babyPoopPage.toolGuideline && (
                  <StyledGuideline>
                    <Heading tag="h4" as="h3">
                      {babyPoopPage.toolGuideline.title}
                    </Heading>
                    <StyledBabyPoopListDescription>
                      <Text size="p2" as="div">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: babyPoopPage.toolGuideline.description.replace(
                              /<(\/)?h1>/gi,
                              '<$1h3>',
                            ),
                          }}
                        />
                      </Text>
                    </StyledBabyPoopListDescription>
                  </StyledGuideline>
                )}
                <StyledBabyPoopContentWarning ref={warning}>
                  <WarningIcon />
                  <Text size="s3" weight="bold">
                    {f({
                      id: 'babyPoop.contentWarningMessage',
                      defaultMessage: 'Content warning: Photosensitivity',
                    })}
                  </Text>
                </StyledBabyPoopContentWarning>
                <StyledBabyPoopListGrid>
                  {babyPoopPage.result.map((item) => (
                    <BabyPoopListItem
                      key={item.name}
                      item={item}
                      link={`${basePath}${getHealthToolLink(
                        HEALTH_TOOL_TYPE.BABY_POOP,
                      )}?type=${item.name}`}
                      wrapperAttributes={{
                        dataEventCategory: 'Health Tool',
                        dataEventAction: 'Baby Stool Option Click',
                        dataEventLabel: `${getMarryBabyUrl(
                          locale,
                        )}${basePath}${getHealthToolLink(
                          HEALTH_TOOL_TYPE.BABY_POOP,
                        )}`,
                        onClick: (e) => {
                          if (!isLoggedIn) {
                            e.preventDefault()
                            redirectToSSOWithReturnUrl(
                              `${getMarryBabyUrl(
                                locale,
                              )}${basePath}${getHealthToolLink(
                                HEALTH_TOOL_TYPE.BABY_POOP,
                              )}?type=${item.name}`,
                            )
                          }
                        },
                      }}
                    />
                  ))}
                </StyledBabyPoopListGrid>
              </StyledContent>

              <StyledDisclamerInfo>
                {babyPoopPage.disclaimer && (
                  <OvulationInfo
                    heading={f({ id: 'ovulationPage.disclaimer' })}
                    content={babyPoopPage.disclaimer}
                    //
                  />
                )}
                {!!babyPoopPage.information?.length && (
                  <StyledBabyPoopInfo
                    heading={f({ id: 'tools.faqs' })}
                    infos={babyPoopPage.information}
                    // className="faqs-block"
                    withDisclaimer={!!babyPoopPage.disclaimer}
                  />
                )}
              </StyledDisclamerInfo>

              {/* {isMobile && (
                <div className="bottom-ad">
                  <AdBlock
                    adUnitPath={adUnitPath}
                    size={[300, 250]}
                    divId={'div-gpt-ad-endbanner-mobile'}
                    pos={['EndBanner']}
                  />
                </div>
              )} */}
            </StyledBabyPoopIntroduceMainColumn>
          }
          rightColumn={
            !isMobile ? (
              <StyledBabyPoopIntroduceSubColumn className="pl-16 my-40">
                <div className="top-ad">
                  <AdBlock
                    adUnitPath={adUnitPath}
                    size={[
                      [300, 250],
                      [300, 600],
                    ]}
                    divId={'div-gpt-ad-midbanner1-desktop'}
                    pos={['MidBanner1']}
                  />
                </div>
                {/* <div className="mt-40 bottom-ad">
                  <AdBlock
                    adUnitPath={adUnitPath}
                    size={[300, 600]}
                    divId={'div-gpt-ad-endbanner-desktop'}
                    pos={['EndBanner']}
                  />
                </div> */}
              </StyledBabyPoopIntroduceSubColumn>
            ) : undefined
          }
        />
      </Container>

      <FixedBottomAdBlock
        adUnitPath={adUnitPath}
        className="article-bottom-ad"
        isMobile={isMobile}
      />
    </StyledBabyPoopIntroduce>
  )
}

export { BabyPoopIntroduce }
